import styled from '@emotion/styled'

import { cardBorderColor } from '../../../components/advice/shared'
import { pageBackgroundFixed } from '../../../components/pageBackground'
import { PANEL_GUTTER, CommentCard as SharedCommentCard, CommentText } from '../../hey-nic/styled'

/*
 * Below this width the 504px side panel leaves too little room beside the step
 * content (which is itself up to 1000px wide), so the chatbot switches to the
 * mobile treatment: an in-page AI comment at the top of the step that opens a
 * full-screen overlay. It is deliberately NOT the app's MOBILE_VIEWPORT (600px)
 * — a 700px-wide tablet is "desktop" to the rest of SBSA but has no room for a
 * side panel.
 */
export const PANEL_VIEWPORT = '(min-width: 1100px)'
export const PANEL_MEDIA = `@media ${PANEL_VIEWPORT}`

// Design: a 504px card inset 20px from the top nav, the right edge, and the
// bottom. It narrows with the viewport rather than crowding the step content off
// the screen at the low end of PANEL_VIEWPORT.
const PANEL_WIDTH = 'min(504px, 38vw)'

// Fixed header height (design), which also sets the drop-shadow band's depth.
const HEADER_HEIGHT = 70

// PanelCard's 10px radius measured on its INSIDE, past its 2px border. Any filled
// row flush against the card's top or bottom has to carry it: the card doesn't
// clip its contents, so a square corner paints over the inside of the border's
// curve and squares the card off.
const CARD_INNER_RADIUS = 8

const surface = p => p.theme.topNavColor ?? '#ffffff'
const mutedText = p => p.theme.lightestGray ?? '#7a8e96'

/* ── Desktop side panel ─────────────────────────────────────────────── */

// The row that holds the step column and the panel. Positioned so the collapsed
// panel's pull tab can anchor to its right edge and vertical center.
export const AdviceBody = styled.div`
  position: relative;
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
`

// Everything that isn't the panel: progress bar, step content, footer. Shrinks as
// the panel expands, which is what re-centers the step content beside it.
export const AdviceMain = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
`

/*
 * The panel's gap in the layout. Collapsing its width (rather than unmounting the
 * panel) is what makes the open/close transition smooth — the step content slides
 * back out to full width as the gap closes — and it keeps the step's chat mounted
 * so closing the panel never costs the transcript.
 */
export const PanelSlot = styled.div`
  flex: 0 0 auto;
  box-sizing: border-box;
  width: ${p => (p.open ? `calc(${PANEL_WIDTH} + ${PANEL_GUTTER}px)` : '0px')};
  overflow: hidden;
  transition:
    width 0.3s ease,
    opacity 0.2s ease;
  opacity: ${p => (p.open ? 1 : 0)};
  /* Collapsed, it is 0-wide but still mounted: keep it out of the tab order and
     away from the pointer. */
  ${p => (p.open ? '' : 'pointer-events: none;')}
`

// The bordered card itself. Fixed to the slot's open width so its contents don't
// reflow line-by-line while the slot collapses.
export const PanelCard = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: ${PANEL_WIDTH};
  height: calc(100% - ${2 * PANEL_GUTTER}px);
  margin: ${PANEL_GUTTER}px ${PANEL_GUTTER}px ${PANEL_GUTTER}px 0;
  border: 2px solid ${cardBorderColor};
  border-radius: 10px;
  /* No fill: the page's background gradient shows through the card (design). */
`

/*
 * Collapsed state: a half-disc tab flush against the right edge, vertically
 * centered on the panel it replaces. Hover matches the scroll-to-bottom button,
 * per the story.
 *
 * A true half circle, so the corner radius must equal the width AND half the
 * height (design: 60 × 119). `50%` would give a half ELLIPSE — 50% of the width
 * is only a 30px bulge against a 60px vertical radius — so the radius is in px
 * and TAB_RADIUS drives both dimensions to keep them in step. The flat side sits
 * on the screen edge and never shows, hence no right border.
 */
const TAB_RADIUS = 60

export const PullTab = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 8;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${TAB_RADIUS}px;
  height: ${2 * TAB_RADIUS}px;
  /* Design offsets the icon 5px right of center so it sits clear of the curve. */
  padding: 0 0 0 10px;
  border: 1px solid #e6e9ea;
  border-right: none;
  border-radius: ${TAB_RADIUS}px 0 0 ${TAB_RADIUS}px;
  background: ${surface};
  box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: 0.2s;

  & svg {
    display: block;
    width: 30px;
    height: 30px;
  }

  &:hover {
    background: ${p => p.theme.primaryLighter ?? '#d4ebf2'};
    border-color: ${p => p.theme.primaryColor};
  }
`

/* ── Mobile overlay ─────────────────────────────────────────────────── */

/*
 * Full-screen sheet, offset just far enough to leave the mobile progress dashes
 * visible above it (12px + 20px + 6px of AdviceProgressIndicator's mobile
 * container). Wider viewports show the desktop progress bar below the top nav
 * instead, so there the sheet covers everything.
 */
export const OverlaySheet = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  flex-direction: column;
  background: ${surface};
  box-shadow: 0 1px 30px 7px rgba(122, 142, 150, 0.25);

  @media (max-width: 600px) {
    top: 38px;
    border-radius: 20px 20px 0 0;
  }
`

/* ── Shared chat chrome (panel + overlay) ───────────────────────────── */

export const ChatShell = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`

/*
 * Fixed top row. Transparent at rest so the card reads as one surface, gaining a
 * fill and a drop shadow once the transcript scrolls under it (design) — without
 * the fill the shadow would sit over text showing through.
 */
export const ChatHeader = styled.div`
  position: relative;
  z-index: 2;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: ${HEADER_HEIGHT}px;
  padding: 0 ${PANEL_GUTTER}px;
  border-radius: ${CARD_INNER_RADIUS}px ${CARD_INNER_RADIUS}px 0 0;
  transition: box-shadow 0.2s ease;
  background: ${p => (p.scrolled ? surface(p) : 'transparent')};
  box-shadow: ${p => (p.scrolled ? '0 5px 10px 0 rgba(122, 142, 150, 0.25)' : 'none')};
`

// Design: the "HEY NIC" lockup is centered in the header, with the close control
// pinned to the right gutter.
export const HeaderWordmark = styled.img`
  display: block;
  height: 30px;
  width: auto;

  @media (max-width: 600px) {
    height: 25px;
  }
`

export const HeaderCloseButton = styled.button`
  position: absolute;
  right: ${PANEL_GUTTER}px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  & svg {
    width: 21px;
    height: 21px;
  }
  & svg .icon-color {
    fill: ${mutedText};
    stroke: ${mutedText};
  }
`

// The scrolling middle. Part of the min-height: 0 chain the transcript needs to
// shrink instead of pushing the fixed footer off the card.
export const ChatBody = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
`

/*
 * Fixed bottom row: the disclaimer, plus the input field on the summary step.
 *
 * The transcript reaches down over this row by the input pill's corner radius, so
 * that content disappears behind the pill's real rounded shape rather than at an
 * invisible line above it (see Transcript in hey-nic/styled). Outside the summary
 * step there IS no pill — the row is the caption on its own — and that overhang
 * landed on the caption instead, with transcript content drawing straight through
 * the text. `solid` is that case: the row takes a fill of its own, and being
 * positioned it paints over ChatBody, so content goes behind the caption and is
 * hidden exactly as it would be behind the input.
 *
 * The fill has to match what is actually behind the row, and that differs by
 * layout. The mobile sheet carries its own flat surface (OverlaySheet), so a
 * colour is enough. The docked card is deliberately unfilled (PanelCard) and sits
 * straight on AdviceLayout's backdrop — artwork over a colour — so the colour
 * alone would show as a flat patch wherever a tenant's artwork reaches this far
 * down. It repaints the whole backdrop instead, off the same helper AdviceLayout
 * uses and anchored to the viewport, so the two line up pixel for pixel.
 */
export const ChatFooter = styled.div`
  position: relative;
  flex: 0 0 auto;
  padding: 0 ${PANEL_GUTTER}px ${PANEL_GUTTER}px;
  border-radius: 0 0 ${CARD_INNER_RADIUS}px ${CARD_INNER_RADIUS}px;
  background: ${p => (p.solid ? surface(p) : 'transparent')};

  ${PANEL_MEDIA} {
    ${p => (p.solid ? pageBackgroundFixed(p) : 'background: transparent;')}
  }

  @media (max-width: 600px) {
    padding-bottom: max(${PANEL_GUTTER}px, env(safe-area-inset-bottom));
  }
`

/* ── Mobile in-page AI comment (the overlay's entry point) ──────────── */

// The card itself is shared with Home's equivalent (see hey-nic/styled); only
// its place in the step content is SBSA's.
export const CommentCard = styled(SharedCommentCard)`
  /* Sits above the step's own heading, with the design's gap below it. */
  margin-bottom: 30px;
`

export { CommentText }
